import { useState, useEffect, useCallback, useRef } from 'react';
import { VitrinService } from '@/services/vitrin-service';
import { VitrinCategory } from '@/models/home/VitrinCategory';
import CourseCard, { CourseCardSkeleton } from './course-card';
import FilterSection, {
  DEFAULT_SORT,
  RELEVANCE_SORT,
} from './filter-section';
import { VitrinListedCourse } from '@/models/course/VitrinListedCourse';

// Same rhythm as the search results: one column on phones, more as the rail
// widens — the card is a fixed-height row, so it tiles rather than stretches.
const COURSES_GRID = "grid grid-cols-1 gap-3 md:grid md:grid-cols-2 lg:grid-cols-3";

interface MiniappCoursesClientProps {
  miniappId: string;
  categories: VitrinCategory[];
  initialCourses: VitrinListedCourse[];
}

export default function MiniappCoursesClient({
  miniappId,
  categories,
  initialCourses,
}: MiniappCoursesClientProps) {
  const [courses, setCourses] = useState<VitrinListedCourse[]>(initialCourses);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<number[]>([]);
  // Null until the user picks an order, so the sort can keep following the
  // query: searching ranks by relevance, and pinning "newest" on top of a
  // ranked result set throws that ranking away.
  const [chosenSort, setChosenSort] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isFirstMount = useRef<boolean>(true);

  const sortOrder = chosenSort ?? (searchQuery.trim() ? RELEVANCE_SORT : DEFAULT_SORT);

  const fetchCourses = useCallback(async (
    categoryIds: number[],
    sort: string,
    search: string
  ) => {
    setLoading(true);
    try {
      const response = await VitrinService.getMiniAppCourses(miniappId, {
        filter: {
          ...(categoryIds.length > 0 && { category_id: categoryIds }),
          // `global` searches the whole course, `title` only its name — on a
          // shelf of 7 matching courses `title` found 2. Persian normalisation
          // and stemming ride on `global` too.
          ...(search.trim() && { global: search.trim() }),
        },
        // Omitted rather than sent as `relevance`: omitting *is* relevance on
        // the search backend, and an API without that sort key would reject it.
        sort: sort === RELEVANCE_SORT ? undefined : sort,
        page: 1,
        per_page: 15,
      });
      if (response.data) {
        setCourses(response.data);
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
      setCourses([]);
    } finally {
      setLoading(false);
    }
  }, [miniappId]);

  // Debounced search effect
  useEffect(() => {
    // Skip the first mount since we already have initial courses
    if (isFirstMount.current) {
      isFirstMount.current = false;
      return;
    }

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      fetchCourses(selectedCategoryIds, sortOrder, searchQuery);
    }, 500);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchQuery, selectedCategoryIds, sortOrder, fetchCourses]);

  const handleCategoryClick = (categoryId: number) => {
    setSelectedCategoryIds((prev) => {
      if (prev.includes(categoryId)) {
        // Remove category if already selected
        return prev.filter((id) => id !== categoryId);
      } else {
        // Add category if not selected
        return [...prev, categoryId];
      }
    });
  };

  const handleSortChange = (sort: string) => {
    setChosenSort(sort);
  };
  return (
    <>
      <FilterSection
        categories={categories}
        selectedCategoryIds={selectedCategoryIds}
        sortOrder={sortOrder}
        searchQuery={searchQuery}
        onCategoryClick={handleCategoryClick}
        onSortChange={handleSortChange}
        onSearchChange={setSearchQuery}
        loading={loading}
      />

      <section
        className={`p-4 md:px-6 ${COURSES_GRID}`}
        data-track-section="miniapp_courses"
      >
        {loading && courses.length === 0 ? (
          Array.from({ length: 6 }).map((_, index) => (
            <CourseCardSkeleton key={index} showMiniApp={false} />
          ))
        ) : courses.length === 0 ? (
          <div className="text-center py-8 text-default-600 md:col-span-2 lg:col-span-3">
            دوره‌ای یافت نشد
          </div>
        ) : (
          courses.map((course, index) => (
            <CourseCard
              key={course.id}
              slotIndex={index}
              title={course.title}
              sessionsCount={course.lessons_count}
              studentsCount={course.users_count}
              thumbnail={course.vitrin_cover_url || course.cover_url || course.cover_thumb_url}
              courseId={course.id}
              miniAppUuid={miniappId}
              rating={course.ratings_avg}
              price={course.price}
              specialPrice={course.special_price}
              isFree={course.is_free}
            />
          ))
        )}

      </section>
    </>
  );
}

